import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';

interface AllTimeLeaderboardEntry {
  playerId?: string; // Optional - only included for admin users
  nickname: string;
  totalScore: number;
  gamesPlayed: number;
  totalCorrectAnswers: number;
  totalAnswers: number;
  rank: number;
}

function AllTimeLeaderboard() {
  const navigate = useNavigate();
  const { player } = usePlayer();
  const { user, loading: authLoading } = useAuth();
  
  const [leaderboard, setLeaderboard] = useState<AllTimeLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Redirect if not authenticated (but wait for auth to finish loading)
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('🚫 Not authenticated, redirecting to login');
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  // Fetch all-time leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        // Include requesting user email for admin check
        const queryParams = new URLSearchParams();
        if (user?.email) {
          queryParams.append('requestingUserEmail', user.email);
        }
        const response = await fetch(`${serverUrl}/api/sessions/all-time-leaderboard?${queryParams}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('📊 Fetched all-time leaderboard data:', data);
          setLeaderboard(data.leaderboard);
        } else {
          console.warn('⚠️ Failed to fetch all-time leaderboard:', response.status);
          setError('Failed to load all-time leaderboard');
        }
      } catch (err) {
        console.error('❌ Error fetching all-time leaderboard:', err);
        setError('Failed to load leaderboard');
      } finally {
        setIsLoading(false);
      }
    };
    
    // Only fetch if user is authenticated and not loading
    if (user && !authLoading) {
      fetchLeaderboard();
    }
  }, [user, authLoading]);

  const handleReturnToLobby = () => {
    navigate('/lobby');
  };

  const getRankEmoji = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const getRankClass = (rank: number, isCurrentPlayer: boolean) => {
    if (isCurrentPlayer) {
      return 'bg-primary/10 border-primary text-primary';
    }
    
    switch (rank) {
      case 1: return 'bg-yellow-50 border-yellow-300 text-yellow-900';
      case 2: return 'bg-muted border-border text-foreground';
      case 3: return 'bg-orange-50 border-orange-300 text-orange-900';
      default: return 'bg-muted border-border text-muted-foreground';
    }
  };

  // Show loading state for auth or data
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {authLoading ? 'Checking authentication...' : 'Loading all-time leaderboard...'}
          </p>
        </div>
      </div>
    );
  }

  // Find current player's entry
  const playerEntry = leaderboard.find(entry => entry.playerId === player?.id);
  const highestScore = leaderboard.length > 0 ? leaderboard[0].totalScore : 0;
  const averageScore = leaderboard.length > 0 
    ? Math.round(leaderboard.reduce((sum, entry) => sum + entry.totalScore, 0) / leaderboard.length)
    : 0;

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">🏆 All-Time Leaderboard</h1>
          <p className="text-muted-foreground text-lg">
            Historical rankings across all trivia games
          </p>
          {playerEntry && (
            <p className="text-primary text-sm mt-2">
              Your all-time total: {playerEntry.totalScore.toLocaleString()} points across {playerEntry.gamesPlayed} {playerEntry.gamesPlayed === 1 ? 'game' : 'games'}
            </p>
          )}
        </div>

        {/* Leaderboard */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-center">All-Time Rankings</CardTitle>
            <CardDescription className="text-center">
              Total scores accumulated across all trivia sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-muted-foreground mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No leaderboard data available</h3>
                <p className="text-muted-foreground">{error || 'No games have been played yet.'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry) => {
                  const isCurrentPlayer = entry.playerId === player?.id;
                  const accuracy = entry.totalAnswers > 0 
                    ? Math.round((entry.totalCorrectAnswers / entry.totalAnswers) * 100)
                    : 0;
                  
                  return (
                    <div
                      key={entry.playerId}
                      className={`p-4 rounded-lg border-2 transition-all ${getRankClass(entry.rank, isCurrentPlayer)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-2xl">
                            {getRankEmoji(entry.rank)}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-lg">{entry.nickname}</span>
                              {isCurrentPlayer && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                                  YOU
                                </span>
                              )}
                            </div>
                            {user?.email === 'caleb.brown@x-team.com' && entry.playerId && (
                              <div className="text-xs text-muted-foreground">
                                {entry.playerId}
                              </div>
                            )}
                            <div className="text-sm opacity-75">
                              Rank #{entry.rank} • {entry.gamesPlayed} {entry.gamesPlayed === 1 ? 'game' : 'games'} • {accuracy}% accuracy
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">
                            {entry.totalScore.toLocaleString()}
                          </div>
                          <div className="text-sm opacity-75">
                            total points
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Summary */}
        {leaderboard.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>📊 Overall Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{leaderboard.length}</div>
                  <div className="text-sm text-muted-foreground">Total Players</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {highestScore.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">Highest Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {averageScore.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">Average Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {playerEntry ? `#${playerEntry.rank}` : '-'}
                  </div>
                  <div className="text-sm text-muted-foreground">Your Rank</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center mb-8">
          <Button
            onClick={handleReturnToLobby}
            size="lg"
            className="px-8"
          >
            🏠 Return to Lobby
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-muted-foreground text-sm">
            Rankings based on cumulative performance across all trivia games 🎉
          </p>
        </div>
      </div>
    </div>
  );
}

export default AllTimeLeaderboard;

